import {
  ApplicationData,
  SocketMessage,
  StatusMessage,
  ErrorMessage,
  InterviewCompleteData,
  ReferralData,
} from "../types/interview";
import * as Sentry from "@sentry/react";

export class SocketService {
  private socket: WebSocket | null = null;
  private clientId: string;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 50;
  private reconnectDelay: number = 1000;
  private eventHandlers: Map<string, Function[]> = new Map();
  private isConnecting: boolean = false;
  private shouldReconnect: boolean = true;
  private messageQueue: { type: string; data: any }[] = [];
  private browserEventListenersRegistered = false;
  private disconnectWindow: number[] = [];
  private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;


  constructor() {
    this.clientId = this.generateClientId();
  }

  private handleOnline = () => {
    console.log("Network came back online, attempting to reconnect...");
    Sentry.addBreadcrumb({
      category: "browser.event",
      message: "Network status changed: online",
      level: "info",
    });

    // Network came back - immediately try to reconnect if disconnected
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.log("Reconnecting after network restoration...");
      this.connect().catch((error) => {
        console.error("Failed to reconnect after network came online:", error);
      });
    }
  };

  private handleOffline = () => {
    console.log("Network went offline");
    Sentry.addBreadcrumb({
      category: "browser.event",
      message: "Network status changed: offline",
      level: "warning",
    });

    // Network went offline
  };

  private handleVisibilityChange = () => {
    const isVisible = document.visibilityState === "visible";

    Sentry.addBreadcrumb({
      category: "browser.event",
      message: `Page visibility changed to: ${document.visibilityState}`,
      level: "info",
      data: {
        visibilityState: document.visibilityState,
        isVisible,
      },
    });

    if (isVisible) {
      // Page became visible again - check connection health
      console.log("Page became visible, checking WebSocket connection...");

      // Check if connection is still active
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        console.log("Connection appears active after returning to foreground");
      } else if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
        // No active connection - attempt to reconnect
        console.log("No active connection after returning to foreground, reconnecting...");
        this.connect().catch((error) => {
          console.error("Failed to reconnect after page became visible:", error);
        });
      }
    } else {
      // Page became hidden
      console.log("Page became hidden");
    }
  };

  private handlePageHide = () => {
    Sentry.addBreadcrumb({
      category: "browser.event",
      message: "Page is being hidden or unloaded",
      level: "warning",
    });
  };

  private registerBrowserEventListeners = () => {
    if (this.browserEventListenersRegistered) {
      return;
    }
    window.addEventListener("online", this.handleOnline);
    window.addEventListener("offline", this.handleOffline);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    window.addEventListener("pagehide", this.handlePageHide);
    this.browserEventListenersRegistered = true;
    Sentry.addBreadcrumb({
      category: "browser.event",
      message: "Browser event listeners registered.",
      level: "info",
    });
  };

  private unregisterBrowserEventListeners = () => {
    if (!this.browserEventListenersRegistered) {
      return;
    }
    window.removeEventListener("online", this.handleOnline);
    window.removeEventListener("offline", this.handleOffline);
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    window.removeEventListener("pagehide", this.handlePageHide);
    this.browserEventListenersRegistered = false;
    Sentry.addBreadcrumb({
      category: "browser.event",
      message: "Browser event listeners unregistered.",
      level: "info",
    });
  };

  private generateClientId(): string {
    return "client_" + Math.random().toString(36).substr(2, 9) + "_" + Date.now();
  }

  private getWebSocketUrl(): string {
    // Debug logging
    console.log("Environment variables:", {
      NODE_ENV: process.env.NODE_ENV,
      REACT_APP_WS_URL: process.env.REACT_APP_WS_URL,
      REACT_APP_API_URL: process.env.REACT_APP_API_URL,
    });

    // In production, determine WebSocket URL based on current location
    if (process.env.NODE_ENV === "production" && !process.env.REACT_APP_WS_URL) {
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const url = `${protocol}//${window.location.host}`;
      console.log("Using production WebSocket URL:", url);
      return url;
    }

    // Use environment variable or default to localhost
    const url = process.env.REACT_APP_WS_URL || "ws://localhost:8000";
    console.log("Using WebSocket URL:", url);
    return url;
  }

  /**
   * Returns a safe-to-log WebSocket target descriptor (no path, no client id, no querystring).
   * This is meant for Sentry breadcrumbs and other telemetry.
   */
  private getSafeWsTarget(baseWsUrl: string): { host: string; protocol: string; port?: string } {
    try {
      const parsed = new URL(baseWsUrl);
      // Never include pathname/search/hash; just the origin-ish parts.
      return {
        host: parsed.host, // includes port if present
        protocol: parsed.protocol.replace(":", ""),
        port: parsed.port || undefined,
      };
    } catch {
      // Fallback if baseWsUrl isn't parseable for some reason.
      return { host: "<unknown>", protocol: "<unknown>" };
    }
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.clearReconnectTimer();

      // Prevent multiple simultaneous connection attempts
      if (this.isConnecting || (this.socket && this.socket.readyState === WebSocket.CONNECTING)) {
        console.log("Connection already in progress, skipping...");
        return resolve();
      }

      // If already connected, resolve immediately
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        console.log("Already connected, skipping connection attempt");
        return resolve();
      }

      this.isConnecting = true;
      this.shouldReconnect = true;
      this.registerBrowserEventListeners();

      // Ensure previous connection is closed
      if (this.socket) {
        this.socket.close();
      }

      // Determine WebSocket URL
      const wsUrl = this.getWebSocketUrl();
      const wsTarget = this.getSafeWsTarget(wsUrl);
      const fullWsUrl = `${wsUrl}/ws/${this.clientId}`;

      Sentry.addBreadcrumb({
        category: "websocket.connect",
        message: "Attempting WebSocket connect",
        level: "info",
        data: {
          host: wsTarget.host,
          protocol: wsTarget.protocol,
          ...(wsTarget.port && { port: wsTarget.port }),
          isOnline: navigator.onLine,
          visibilityState: document.visibilityState,
        },
      });

      console.log("WebSocket connection details:", {
        baseUrl: wsUrl,
        clientId: this.clientId,
        fullUrl: fullWsUrl,
      });
      console.log("Connecting to WebSocket:", fullWsUrl);
      this.socket = new WebSocket(fullWsUrl);

      this.socket.onopen = () => {
        console.log("WebSocket connected with client ID:", this.clientId);
        Sentry.addBreadcrumb({
          category: "websocket.open",
          message: "WebSocket connected",
          level: "info",
          data: {
            host: wsTarget.host,
            protocol: wsTarget.protocol,
            ...(wsTarget.port && { port: wsTarget.port }),
          },
        });
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.clearReconnectTimer();
        this.emitEvent("connection_established", { clientId: this.clientId });
        this.processMessageQueue(); // Process any queued messages
        resolve();
      };

      this.socket.onerror = (error) => {
        console.error("WebSocket connection error:", error);
        Sentry.addBreadcrumb({
          category: "websocket.error",
          message: "WebSocket onerror",
          level: "warning",
          data: {
            host: wsTarget.host,
            protocol: wsTarget.protocol,
            ...(wsTarget.port && { port: wsTarget.port }),
            isOnline: navigator.onLine,
            visibilityState: document.visibilityState,
            readyState: this.socket?.readyState,
          },
        });
        // Avoid captureException; the onclose handler will classify the outcome
        this.isConnecting = false;
        // IMPORTANT: browsers pass a DOM Event here (often `Event { type: "error" }`).
        // Rejecting with a non-Error object is a common source of "<unknown> captured as promise rejection"
        // in Sentry because it yields no stacktrace.
        const err =
          error instanceof ErrorEvent && error.error instanceof Error
            ? error.error
            : new Error("WebSocket connection error");
        // Preserve original event for debugging without leaking it as the rejection reason.
        (err as unknown as { cause?: unknown }).cause ??= error;
        reject(err);
      };

      this.socket.onclose = (event) => {
        const isOnline = navigator.onLine;
        const isVisible = document.visibilityState === "visible";
        console.log("WebSocket disconnected:", event.code, event.reason, { isOnline, isVisible });

        // Track recent disconnects (timestamps in ms) within a 2-minute window
        const now = Date.now();
        this.disconnectWindow.push(now);
        const twoMinutesAgo = now - 120000;
        this.disconnectWindow = this.disconnectWindow.filter((ts) => ts >= twoMinutesAgo);

        Sentry.addBreadcrumb({
          category: "websocket.close",
          message: `WebSocket disconnected. Code: ${event.code}`,
          level: event.code === 1000 ? "info" : "warning",
          data: { code: event.code, reason: event.reason, isOnline, isVisible },
        });

        const repeatedVisibleOnlineDisconnect =
          isOnline && isVisible && this.disconnectWindow.length >= 3;
        const reason = (event.reason || "").toLowerCase();

        if (event.code === 1000 || event.code === 1001 || event.code === 1012) {
          // Normal/going away/service restart: no capture
        } else if (event.code === 1006) {
          // Abnormal: capture as warning only when user is online, visible, and repeated
          if (repeatedVisibleOnlineDisconnect) {
            Sentry.captureMessage("WebSocket 1006 while visible/online", "warning");
          }
        } else if (event.code === 1011 && reason.includes("keepalive ping timeout")) {
          if (repeatedVisibleOnlineDisconnect) {
            Sentry.captureMessage(
              "WebSocket 1011 keepalive ping timeout while visible/online",
              "warning"
            );
          }
        } else {
          Sentry.captureMessage(
            `WebSocket disconnected: code=${event.code} reason=${event.reason}`,
            "warning"
          );
        }

        this.isConnecting = false;
        this.emitEvent("disconnect", { code: event.code, reason: event.reason });

        // Attempt reconnection for non-normal closes
        if (this.shouldReconnect && event.code !== 1000) {
          // For 1012 (Service Restart), reconnect immediately with minimal jitter
          if (event.code === 1012) {
             console.log("Service restart detected (1012), reconnecting immediately...");
             this.reconnectAttempts = 0; // Reset attempts to allow fast reconnect
             this.handleReconnect(true); // fast reconnect
          } else {
             this.handleReconnect();
          }
        }
      };

      this.socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          const messageString = JSON.stringify(message);
          Sentry.addBreadcrumb({
            category: "websocket.receive",
            message: `Received message type: ${message.type}`,
            level: "info",
            data: {
              message: messageString.substring(0, 300), // Truncate
            },
          });
          this.handleMessage(message);
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
        }
      };
    });
  }

  private handleReconnect(isFast: boolean = false): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts && this.shouldReconnect) {
      if (this.reconnectTimeoutId !== null) {
        console.log("Reconnect already scheduled, skipping duplicate timer");
        return;
      }

      this.reconnectAttempts++;
      // If fast reconnect, use minimal delay (500ms) + small jitter
      const base = isFast 
        ? 500 
        : Math.min(30000, this.reconnectDelay * Math.pow(1.5, this.reconnectAttempts));
        
      const jitter = Math.floor(Math.random() * 1000);
      console.log(
        `Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${
          base + jitter
        }ms...`
      );
      this.reconnectTimeoutId = setTimeout(() => {
        this.reconnectTimeoutId = null;
        if (this.shouldReconnect) {
          this.connect().catch((error) => {
            console.error("Reconnection failed:", error);
          });
        }
      }, base + jitter);
    } else {
      console.error("Max reconnection attempts reached or reconnection disabled");
      this.emitEvent("reconnect_failed", {});
    }
  }

  private emitEvent(type: string, data: any): void {
    const handlers = this.eventHandlers.get(type) || [];
    handlers.forEach((handler) => {
      try {
        handler(data);
      } catch (error) {
        console.error(`Error in ${type} handler:`, error);
      }
    });
  }

  private handleMessage(message: any): void {
    const { type, data } = message;
    
    // CRITICAL FIX: Automatically respond to server pings with pong
    // This is required for the WebSocket Health Monitor to work correctly.
    // Without this, the health monitor marks connections as broken after 90s.
    if (type === "ping") {
      // Respond immediately with pong to keep connection healthy
      this.sendMessage("pong", {
        timestamp: data.timestamp || Date.now(),
        server_time: data.server_time,
      });
      console.log("SocketService: Automatically responded to server ping with pong");
      // Don't emit ping event - just respond and return
      return;
    }
    
    // Emit other events normally
    this.emitEvent(type, data);
  }

  disconnect(): void {
    this.unregisterBrowserEventListeners();
    this.shouldReconnect = false;
    this.clearReconnectTimer();
    if (this.socket) {
      this.socket.close(1000, "Normal closure");
      this.socket = null;
    }
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimeoutId !== null) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
  }

  private sendMessage(type: string, data: any): void {
    const message = { type, data };
    const messageString = JSON.stringify(message);

    Sentry.addBreadcrumb({
      category: "websocket.send",
      message: `Sending message type: ${type}`,
      level: "info",
      data: {
        message: messageString.substring(0, 300), // Truncate to prevent large payloads
      },
    });

    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      try {
        console.log("SocketService: Sending WebSocket message:", message);
        this.socket.send(messageString);
        console.log("SocketService: Message sent successfully");
      } catch (error) {
        console.error("SocketService: Error sending message, queuing.", error);
        Sentry.captureException(error);
        this.messageQueue.push(message);
      }
    } else {
      console.warn("SocketService: WebSocket not connected, queuing message:", message);
      this.messageQueue.push(message);
    }
  }

  private processMessageQueue(): void {
    // Add a guard to prevent an infinite loop if this is called when the socket is not ready.
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.log("Socket not ready, skipping message queue processing.");
      return;
    }

    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      if (message) {
        this.sendMessage(message.type, message.data);
      }
    }
  }

  // Event emitters
  startInterviewWithId(data: ApplicationData): void {
    this.sendMessage("start_interview_with_id", data);
  }

  sendUserMessage(text: string, inputMethod?: string, questionIndex?: number): void {
    const messageData: {
      text: string;
      input_method?: string;
      question_index?: number;
    } = { text };
    if (inputMethod) {
      messageData.input_method = inputMethod;
    }
    if (Number.isInteger(questionIndex)) {
      messageData.question_index = questionIndex;
    }
    this.sendMessage("user_message", messageData);
  }

  sendSalaryResponse(salaryInput: string): void {
    this.sendMessage("salary_response", { salary_input: salaryInput });
  }

  submitBooleanAnswer(questionIndex: number, answer: boolean): void {
    this.sendMessage("submit_boolean_answer", {
      question_index: questionIndex,
      answer: answer,
    });
  }

  submitRating(rating: number): void {
    this.sendMessage("submit_rating", { rating });
  }

  submitReferral(referralData: ReferralData): void {
    this.sendMessage("submit_referral", referralData);
  }

  submitFeedback(feedback: string): void {
    this.sendMessage("submit_feedback", { feedback });
  }

  requestTTS(text: string): void {
    this.sendMessage("request_tts", { text });
  }

  // Event listeners
  private addEventListener(eventType: string, callback: Function): () => void {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, []);
    }

    const handlers = this.eventHandlers.get(eventType)!;
    handlers.push(callback);

    // Return unsubscribe function
    return () => {
      const index = handlers.indexOf(callback);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    };
  }

  // Event listener methods
  onStatus(callback: (data: StatusMessage) => void): () => void {
    return this.addEventListener("status", callback);
  }

  onError(callback: (data: ErrorMessage) => void): () => void {
    return this.addEventListener("error", callback);
  }

  onAiMessage(callback: (data: SocketMessage) => void): () => void {
    return this.addEventListener("ai_message", callback);
  }

  onInterviewComplete(callback: (data: InterviewCompleteData) => void): () => void {
    return this.addEventListener("interview_complete", callback);
  }

  onAnalysisComplete(callback: (data: any) => void): () => void {
    return this.addEventListener("analysis_complete", callback);
  }

  onInterviewAlreadyCompleted(callback: (data: any) => void): () => void {
    return this.addEventListener("interview_already_completed", callback);
  }

  onReferralData(callback: (data: ReferralData) => void): () => void {
    return this.addEventListener("referral_data", callback);
  }

  onRemoveOverlay(callback: (data: { id: string }) => void): () => void {
    return this.addEventListener("remove_overlay", callback);
  }

  onConnectionEstablished(callback: (data: any) => void): () => void {
    return this.addEventListener("connection_established", callback);
  }

  onDisconnect(callback: (data: { code: number; reason: string }) => void): () => void {
    return this.addEventListener("disconnect", callback);
  }

  onPong(callback: (data: any) => void): () => void {
    return this.addEventListener("pong", callback);
  }

  onPing(callback: (data: any) => void): () => void {
    return this.addEventListener("ping", callback);
  }

  onReconnectFailed(callback: () => void): () => void {
    return this.addEventListener("reconnect_failed", callback);
  }

  onPartialToken(callback: (data: { text: string }) => void): () => void {
    return this.addEventListener("partial_token", callback);
  }

  onAudioChunk(callback: (data: any) => void): () => void {
    return this.addEventListener("audio_chunk", callback);
  }

  removeAllListeners(): void {
    this.eventHandlers.clear();
  }

  isConnected(): boolean {
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

  getId(): string {
    return this.clientId;
  }
}

// Export a singleton instance
export const socketService = new SocketService();
